import React, { useRef, useState } from 'react';
import { createSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Captcha, ToolBar } from '@1c/ui';
import { useNavigate } from 'src/utils/useNavigate';
import { FinalField, FinalForm } from 'src/components/FinalForm';
import Button from 'src/components/Button';
import InputField from 'src/components/InputField';
import {
  getAuthorizationStateAPI,
  postAuthorizationStartAPI,
  postConfirmByPasswordAPI
} from 'src/serverApi/authorization';
import { getAuthorizationInfoAPI } from 'src/serverApi/authorization';
import { postAcceptInvitationToOrganizationAPI } from 'src/serverApi/organizationUsers';
import routes from 'src/constants/routes';
import PhoneInput from 'src/components/PhoneInput';
import styles from './styles.module.scss';

type Props = {
  returnUrl?: string | null | undefined;
  inviteOrganizationUeid?: string;
  inviteConfirmationId?: string | null | undefined;
};

// авторизация проходит в 2 этапа (так сложилось на бэкенде и решено было api не менять):
// сначала происходит попытка авторизовать пользователя по номеру телефона на блюре инпута логина,
// а потом уже если требуется пароль, то пользователь вводит пароль и на бэкенд отправляется второй запрос
// при сабмите формы
const Form: React.FC<Props> = ({ returnUrl, inviteConfirmationId, inviteOrganizationUeid}) => {
  const navigate = useNavigate();
  const captcha = useRef<any>();

  const [isLoading, setIsLoading] = useState(false);
  const [authorizationError, setAuthorizationError] = useState('');
  const [authorizationState, setAuthorizationState] = useState<any>(null);
  const [captchaError, setCaptchaError] = useState('');
  
  const resetCaptcha = () => {
    captcha.current?.refresh();
  };
  
  const startAuthorization = async (phone: any) => {
    if (!phone || phone === '+7') {
      setAuthorizationState(null);
      setAuthorizationError('');
      return false;
    }
    
    try {
      setAuthorizationError('');
      setIsLoading(true);
      
      const sessionState = await postAuthorizationStartAPI(phone);
      const authorizationInfo = await getAuthorizationInfoAPI();
      
      if (sessionState?.authorities?.length > 0) {
        // если авторизация происходит по ссылке приглашению, то нужно отправить запрос на подтверждение приглашения
        if (inviteOrganizationUeid && inviteConfirmationId) {
          try {
            await postAcceptInvitationToOrganizationAPI(inviteOrganizationUeid, inviteConfirmationId);
          } catch (error: any) {
            const message = error.type === 'NOT_FOUND'
              ? 'Ссылка недействительна. Получите новую ссылку и перейдите по ней.'
              : 'Произошла ошибка подтверждения приглашения. Попробуйте позже.';
            toast.error(message);
          }
        }
        
        // у пользователя появилась активная сессия с некоторыми правами - направляем в агент или по returnUrl
        navigate(returnUrl ? returnUrl : routes.agent);
        return false;
      }
      
      if (!authorizationInfo?.hasPassword) {
        // для введенного номера телефона может не быть пароля по 2 причинам:
        // 1. для номера телефона еще не создавался аккаунт
        // 2. для номера телефона создавался аккаунт, но был пропущен шаг установки пароля
        // при обсуждении с разработчиком бэкенда было решено, что для пользователя это означает одно и то же -
        // аккаунта нет и надо зарегистрировать аккаунт с данным номером телефона с нуля.
        // при этом для системы повторная регистрация будет являться только назначением пароля уже имеющемуся аккаунту
        setAuthorizationError('Пользователь с таким номером телефона не найден');
        setAuthorizationState(null);
        return false;
      }
  
      // если ошибок нет и для введенного телефона есть аккаунт с паролем, то оставляем пользователя на форме
      // для введения пароля
      const newAuthorizationState = await getAuthorizationStateAPI();
      const passwordCaptchaRequiredAppear =
        !authorizationState?.passwordCaptchaRequired
        && newAuthorizationState?.passwordCaptchaRequired;
      
      setAuthorizationState(newAuthorizationState);
      return !passwordCaptchaRequiredAppear;
    } catch (error: any) {
      if (error.type === 'BAD_REQUEST') {
        // это валидационная ошибка, при этом у нее такой тип, потому что разработчик бэкенда решил,
        // что других ошибок в этом запросе быть не может, поэтому и так нормально
        setAuthorizationError('Неверный формат, номер должен содержать только цифры и иметь длину не меньше 7 символов');
        setAuthorizationState(null);
        return false;
      }
      if (error.type === 'PHONE_PROCESSING_EXCEPTION') {
        setAuthorizationError('Не удалось отправить СМС на указанный номер. Проверьте правильность введенного номера телефона или повторите попытку позже');
        setAuthorizationState(null);
        return;
      }
      toast.error('Произошла ошибка. Попробуйте позже.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const sendPassword = async (data: any) => {
    try {
      setIsLoading(true);
      const confirmInfo = await postConfirmByPasswordAPI(data);
      
      // если бэк вернул confirmedByPassword значит авторизация прошла успешно.
      // иначе бэкенд запоминает неудачную попытку и в ответе передает информацию
      // нужно ли отображать капчу
      if (confirmInfo?.confirmedByPassword) {
        // если авторизация происходит по ссылке приглашению, то нужно отправить запрос на подтверждение приглашения
        if (inviteOrganizationUeid && inviteConfirmationId) {
          try {
            await postAcceptInvitationToOrganizationAPI(inviteOrganizationUeid, inviteConfirmationId);
          } catch (error: any) {
            const message = error.type === 'NOT_FOUND'
              ? 'Ссылка недействительна. Получите новую ссылку и перейдите по ней.'
              : 'Произошла ошибка подтверждения приглашения. Попробуйте позже.';
            toast.error(message);
          }
        }
        
        return navigate(returnUrl ? returnUrl : routes.agent);
      }
      
      setAuthorizationState(confirmInfo);
      toast.error('Неверный пароль');
    } catch (error) {
      const { type } = error as any;
      
      if (type === 'INVALID_CAPTCHA') {
        return setCaptchaError('Введённые символы не совпадают с изображением.');
      }
      
      toast.error('Произошла ошибка. Попробуйте позже.');
    } finally {
      resetCaptcha();
      setIsLoading(false);
    }
  };
  
  const authorize = async (data: any) => {
    const { phone, ...passwordData } = data || {};
    const authorizationStarted = await startAuthorization(phone);
    if (authorizationStarted) {
      await sendPassword(passwordData);
    }
  }
  
  const handleSubmit = (values: any) => {
    authorize(values);
  };
  
  const handleBack = () => {
    if (inviteConfirmationId) {
      navigate({
        pathname: routes.acceptInvitation,
        search: createSearchParams({ confirmation: inviteConfirmationId }).toString(),
      });
    } else if (returnUrl) {
      navigate(returnUrl);
    }else {
      navigate(routes.main);
    }
  };

  return (
    <FinalForm
      onSubmit={handleSubmit}
      submitting={isLoading}
      pristine
      initialValues={{ phone: undefined, password: undefined, captcha: undefined }}
    >
      {({ submitting }: { submitting: any; pristine: any }) => {
        return (
          <div className={styles['form']}>
            <FinalField name="phone">
              {({ value, onChange }: { value: any, onChange: any }) => (
                <PhoneInput
                  value={value}
                  onChange={(params) => {
                    setAuthorizationError('');
                    onChange(params);
                  }}
                  label="Телефон"
                  error={authorizationError}
                />
              )}
            </FinalField>
            <FinalField name="password">
              {({ onChange }: { onChange: any }) => (
                <InputField
                  onChange={onChange}
                  type="password"
                  label="Пароль"
                  className={styles['input']}
                />
              )}
            </FinalField>
            {authorizationState?.passwordCaptchaRequired && (
              <FinalField name="captcha">
                {({ onChange }: { onChange: any }) => (
                  <div className={styles['captcha-wrapper']}>
                    <Captcha ref={captcha} url="/ui/captcha/image.jpg" className={styles['captcha']} />
                    <InputField
                      onChange={onChange}
                      label="Введите символы с картинки"
                      className={styles['input']}
                      error={captchaError}
                    />
                  </div>
                )}
              </FinalField>
            )}
            <ToolBar align="center" className={styles['buttons']}>
              <Button variant="text" disabled={submitting} onClick={handleBack}>
                Назад
              </Button>
              <Button disabled={submitting} type="submit">
                Войти
              </Button>
            </ToolBar>
          </div>
        );
      }}
    </FinalForm>
  );
};

export default Form;
